import { motion, AnimatePresence } from 'motion/react';
import type { MonthGroup as MonthGroupType } from '../utils/groupByMonth';
import { getPlaceCountLabel } from '../utils/groupByMonth';
import { JournalEntry } from './JournalEntry';
import { DURATION_NORMAL } from '../utils/animationTiming';

interface MonthGroupProps {
  group: MonthGroupType;
  onShowOnMap: (placeId: string) => void;
  highlightedPlaceId?: string | null;
  entryRefs?: Map<string, HTMLDivElement>;
  isCollapsed?: boolean;
  onToggleCollapse?: (monthKey: string) => void;
}

export function MonthGroup({
  group,
  onShowOnMap,
  highlightedPlaceId,
  entryRefs,
  isCollapsed = false,
  onToggleCollapse,
}: MonthGroupProps) {
  const handleToggle = () => {
    onToggleCollapse?.(group.key);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <div
      className="mb-6"
      data-testid={`month-group-${group.key}`}
    >
      {/* Month Header - Clickable for collapse */}
      <button
        type="button"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-expanded={!isCollapsed}
        aria-controls={`month-content-${group.key}`}
        className="w-full flex items-center gap-3 mb-4 pl-6 cursor-pointer hover:bg-white/5 rounded-lg py-2 -my-2 transition-colors min-h-[44px]"
        data-testid={`month-header-${group.key}`}
      >
        {/* Chevron indicator */}
        <motion.span
          className="text-gray-400 flex-shrink-0"
          animate={{ rotate: isCollapsed ? -90 : 0 }}
          transition={{ duration: DURATION_NORMAL / 1000 }}
          aria-hidden="true"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="transform"
          >
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.span>

        {/* Timeline dot for month header */}
        <div
          className="w-3 h-3 rounded-full bg-primary/50 ring-2 ring-primary/30 flex-shrink-0"
          aria-hidden="true"
          data-testid="month-header-timeline-dot"
        />

        {/* Month label and count */}
        <div className="flex items-baseline gap-2 text-left">
          <h2 className="text-lg font-semibold text-white">
            {group.displayName}
          </h2>
          <span className="text-sm text-gray-500">
            {getPlaceCountLabel(group.count)}
          </span>
        </div>
      </button>

      {/* Collapsible content */}
      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.div
            id={`month-content-${group.key}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: DURATION_NORMAL / 1000, ease: 'easeInOut' }}
            className="overflow-hidden"
            data-testid={`month-content-${group.key}`}
          >
            {/* Entries within this month */}
            <div className="space-y-4">
              {group.places.map((place) => (
                <JournalEntry
                  key={place.id}
                  ref={(el) => {
                    if (entryRefs) {
                      if (el) {
                        entryRefs.set(place.id, el);
                      } else {
                        entryRefs.delete(place.id);
                      }
                    }
                  }}
                  place={place}
                  onShowOnMap={() => onShowOnMap(place.id)}
                  isHighlighted={highlightedPlaceId === place.id}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
